import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { AiFillStar, AiOutlineStar } from 'react-icons/ai';
import { MdAddShoppingCart, MdRemoveRedEye } from 'react-icons/md';
import { api } from '../../api';
import logger from '../../utils/logger';
import ProductUnavailableModal from '../ProductUnavailableModal';

const ProductCard = ({ product, onAddToCart }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isHovered, setIsHovered] = useState(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [unavailableProduct, setUnavailableProduct] = useState(null);
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    
    // ✅ Check if user is logged in first
    if (!user || !user.userId) {
      logger.warn('⚠️ User not logged in - redirecting to login page');
      alert('Please login to add products to cart');
      navigate('/');
      return;
    }
    
    try {
      setIsCheckingAvailability(true);
      logger.log(`🛒 Checking availability for product ${product.id}...`);

      const response = await api.post('/aastrog_cart/products/check-availability', {
        productId: product.id,
        quantity: 1
      });

      if (response.data.success && response.data.available) {
        // Product is available - add to cart in database
        logger.log(`✅ Product ${product.id} is available - adding to cart`);
        
        const userId = user.userId;
        console.log('📦 Adding to cart with userId:', userId);

        // Call API to add item to cart
        const addResponse = await api.post('/aastrog_cart/cart/add', {
          userId: Number(userId),
          productId: product.id,
          quantity: 1
        });

        if (addResponse.data.success) {
          logger.log(`✅ Product added to cart successfully`);
          console.log('✅ Add to cart response:', addResponse.data);
          
          // Call parent callback if provided
          if (onAddToCart) {
            onAddToCart(product);
          }

          // Navigate to cart page immediately
          console.log('🛒 Navigating to cart...');
          navigate('/cart');
        } else {
          logger.error('❌ Failed to add product to cart');
          console.error('Add to cart error:', addResponse.data);
          alert('❌ Failed to add product to cart. Please try again.');
        }
      } else {
        // Product is NOT available - show modal
        logger.warn(`❌ Product ${product.id} is not available`);
        setUnavailableProduct({
          productName: product.name,
          category: product.category,
          price: product.price,
          stockQty: product.stockQty
        });
        setShowUnavailableModal(true);
      }
    } catch (error) {
      logger.error('❌ Error adding to cart:', error);
      console.error('Cart error details:', error.response?.data || error.message);
      alert('❌ Error adding to cart. Please try again.');
    } finally {
      setIsCheckingAvailability(false);
    }
  };;

  const handleViewDetails = () => {
    console.log(`Viewing details for ${product.id}`);
    navigate(`/product/${product.id}`, { state: { product } });
  };

  const renderStars = (rating) => {
    return (
      <div className="product-stars">
        {[...Array(5)].map((_, i) => (
          <span key={i} className="star-icon">
            {i < Math.floor(rating) ? (
              <AiFillStar size={16} color="#ffd700" />
            ) : (
              <AiOutlineStar size={16} color="#d4af37" />
            )}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div
      className="product-card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleViewDetails}
    >
      {/* Product Image Container */}
      <div className="product-image-wrapper">
        <img
          src={product.image}
          alt={product.name}
          className="product-image"
        />

        {/* Badge */}
        {product.badge && (
          <div className={`product-badge ${product.badge.toLowerCase().replace(' ', '-')}`}>
            {product.badge}
          </div>
        )}

        {/* Overlay on Hover */}
        {isHovered && (
          <div className="product-overlay">
            <div className="overlay-actions">
              <button
                className="btn-view-details"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewDetails();
                }}
              >
                <MdRemoveRedEye size={18} /> View Details
              </button>
              <button
                className="btn-add-cart"
                onClick={handleAddToCart}
                disabled={isCheckingAvailability}
              >
                <MdAddShoppingCart size={18} /> {isCheckingAvailability ? 'Checking...' : 'Add to Cart'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="product-info">
        <h3 className="product-name">{product.name}</h3>

        {/* Rating */}
        <div className="product-rating">
          {renderStars(product.rating)}
          <span className="rating-number">{Number(product.rating).toFixed(1)}</span>
          <span className="review-count">({product.reviews})</span>
        </div>

        {/* Category Tag */}
        <span className="product-category">{product.category}</span>

        {/* Price with MRP and Discount */}
        <div className="product-price-section">
          <div className="product-price">
            <span className="price-amount">₹{product.price}</span>
            {product.discountValue > 0 && (
              <>
                <span className="mrp-price">₹{(product.price + (product.price * product.discountValue) / 100).toFixed(0)}</span>
                <span className="discount-badge">{product.discountValue}% OFF</span>
              </>
            )}
          </div>
        </div>

        {/* Remaining Quantity - Show only if less than 5 */}
        {product.stockQty > 0 && product.stockQty < 5 && (
          <div className="quantity-warning">
            ⚠️ Only {product.stockQty} left
          </div>
        )}

        {/* Mobile Add to Cart */}
        <button
          className="btn-add-cart-mobile"
          onClick={handleAddToCart}
          disabled={isCheckingAvailability}
        >
          <MdAddShoppingCart size={16} /> {isCheckingAvailability ? 'Checking...' : 'Add to Cart'}
        </button>
      </div>

      {/* Product Unavailable Modal */}
      <ProductUnavailableModal
        product={unavailableProduct}
        isOpen={showUnavailableModal}
        onClose={() => setShowUnavailableModal(false)}
      />
    </div>
  );
};

export default ProductCard;
